import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, Shield } from 'lucide-react';
import { useAuth } from './AuthContext';
import { getAuthConfig } from './amplify-config';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/cn';

interface LoginPageProps {
  className?: string;
}

export function LoginPage({ className }: LoginPageProps) {
  const { login, isLoading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const authConfig = getAuthConfig();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    try {
      await login(email, password);
    } catch {
      // Error is already set in AuthContext
    }
  };

  const displayError = localError || error;

  return (
    <div
      className={cn(
        'min-h-screen flex items-center justify-center bg-bg px-4',
        className
      )}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-action-container mb-3">
            <Shield className="w-6 h-6 text-action" />
          </div>
          <h1 className="text-page-title text-text">
            PerMile Admin
          </h1>
          <p className="text-body-sm text-text-secondary mt-1">
            Sign in to the admin dashboard
          </p>
        </div>

        {/* Login Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-surface rounded-card p-5 shadow-card border border-border"
        >
          {/* Error Message */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-4 p-3 rounded-lg bg-loss-container border border-loss/20 flex items-start gap-2"
            >
              <AlertCircle className="w-4 h-4 text-loss flex-shrink-0 mt-0.5" />
              <p className="text-body-sm text-loss">{displayError}</p>
            </motion.div>
          )}

          {/* Email Field */}
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block text-label text-text mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-button border text-body',
                'bg-surface',
                'border-border',
                'text-text',
                'placeholder:text-text-muted',
                'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
                'transition-colors duration-fast'
              )}
              placeholder="admin@haulkeep.com"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          {/* Password Field */}
          <div className="mb-5">
            <label
              htmlFor="password"
              className="block text-label text-text mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-button border text-body',
                'bg-surface',
                'border-border',
                'text-text',
                'placeholder:text-text-muted',
                'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
                'transition-colors duration-fast'
              )}
              placeholder="Password"
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="md"
            className="w-full justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* Environment indicator (dev only) */}
        {import.meta.env.DEV && (
          <div className="mt-4 text-center text-caption text-text-muted">
            <p>Environment: {authConfig.environment}</p>
            <p className="font-mono text-[10px] mt-0.5">
              Pool: {authConfig.userPoolId}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
